/**
 * LocalStorageService - Implementación del StoragePort usando localStorage
 *
 * Esta implementación concreta del StoragePort utiliza localStorage del navegador.
 * Proporciona métodos seguros con manejo de errores y validaciones.
 */

import { Injectable } from '@angular/core';
import { CurrentUser, StorageKeys, StoragePort } from '../../application/ports/storage.port';

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService extends StoragePort {

    /**
     * Verifica si localStorage está disponible
     */
    private isLocalStorageAvailable(): boolean {
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    // ==================== Usuario ====================

    getCurrentUser(): CurrentUser | null {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return null;
            }

            const userStr = localStorage.getItem(StorageKeys.CURRENT_USER);
            if (!userStr) {
                return null;
            }

            const user = JSON.parse(userStr) as CurrentUser;

            // Validar que tenga las propiedades mínimas requeridas
            if (!user.usuario || !user.empresaId) {
                console.warn('Usuario en localStorage tiene datos incompletos');
                return null;
            }

            return user;
        } catch (error) {
            console.error('Error al obtener usuario actual:', error);
            return null;
        }
    }

    setCurrentUser(user: CurrentUser): void {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return;
            }

            // Validar datos mínimos
            if (!user.usuario || !user.empresaId) {
                throw new Error('Usuario debe tener al menos usuario y empresaId');
            }

            const userStr = JSON.stringify(user);
            localStorage.setItem(StorageKeys.CURRENT_USER, userStr);
        } catch (error) {
            console.error('Error al guardar usuario:', error);
            throw error;
        }
    }

    clearCurrentUser(): void {
        try {
            if (this.isLocalStorageAvailable()) {
                localStorage.removeItem(StorageKeys.CURRENT_USER);
            }
        } catch (error) {
            console.error('Error al limpiar usuario:', error);
        }
    }

    // ==================== Token ====================

    getAccessToken(): string | null {
        try {
            if (!this.isLocalStorageAvailable()) {
                return null;
            }

            return localStorage.getItem(StorageKeys.ACCESS_TOKEN);
        } catch (error) {
            console.error('Error al obtener access token:', error);
            return null;
        }
    }

    setAccessToken(token: string): void {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return;
            }

            if (!token || token.trim().length === 0) {
                throw new Error('Token no puede estar vacío');
            }

            localStorage.setItem(StorageKeys.ACCESS_TOKEN, token);
        } catch (error) {
            console.error('Error al guardar access token:', error);
            throw error;
        }
    }

    clearAccessToken(): void {
        try {
            if (this.isLocalStorageAvailable()) {
                localStorage.removeItem(StorageKeys.ACCESS_TOKEN);
            }
        } catch (error) {
            console.error('Error al limpiar access token:', error);
        }
    }

    getRefreshToken(): string | null {
        try {
            if (!this.isLocalStorageAvailable()) {
                return null;
            }

            return localStorage.getItem(StorageKeys.REFRESH_TOKEN);
        } catch (error) {
            console.error('Error al obtener refresh token:', error);
            return null;
        }
    }

    setRefreshToken(token: string): void {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return;
            }

            if (!token || token.trim().length === 0) {
                throw new Error('Refresh token no puede estar vacío');
            }

            localStorage.setItem(StorageKeys.REFRESH_TOKEN, token);
        } catch (error) {
            console.error('Error al guardar refresh token:', error);
            throw error;
        }
    }

    clearRefreshToken(): void {
        try {
            if (this.isLocalStorageAvailable()) {
                localStorage.removeItem(StorageKeys.REFRESH_TOKEN);
            }
        } catch (error) {
            console.error('Error al limpiar refresh token:', error);
        }
    }

    // ==================== Datos Genéricos ====================

    setItem<T>(key: string, value: T): void {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return;
            }

            if (!key || key.trim().length === 0) {
                throw new Error('Key no puede estar vacía');
            }

            const serialized = JSON.stringify(value);
            localStorage.setItem(key, serialized);
        } catch (error) {
            console.error(`Error al guardar item '${key}':`, error);
            throw error;
        }
    }

    getItem<T>(key: string): T | null {
        try {
            if (!this.isLocalStorageAvailable()) {
                return null;
            }

            const item = localStorage.getItem(key);
            if (!item) {
                return null;
            }

            return JSON.parse(item) as T;
        } catch (error) {
            console.error(`Error al obtener item '${key}':`, error);
            return null;
        }
    }

    removeItem(key: string): void {
        try {
            if (this.isLocalStorageAvailable()) {
                localStorage.removeItem(key);
            }
        } catch (error) {
            console.error(`Error al eliminar item '${key}':`, error);
        }
    }

    hasItem(key: string): boolean {
        try {
            if (!this.isLocalStorageAvailable()) {
                return false;
            }

            return localStorage.getItem(key) !== null;
        } catch (error) {
            console.error(`Error al verificar item '${key}':`, error);
            return false;
        }
    }

    // ==================== Operaciones Globales ====================

    clear(): void {
        try {
            if (this.isLocalStorageAvailable()) {
                localStorage.clear();
            }
        } catch (error) {
            console.error('Error al limpiar localStorage:', error);
        }
    }

    getAllKeys(): string[] {
        try {
            if (!this.isLocalStorageAvailable()) {
                return [];
            }

            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    keys.push(key);
                }
            }
            return keys;
        } catch (error) {
            console.error('Error al obtener todas las claves:', error);
            return [];
        }
    }

    getSize(): number {
        try {
            if (!this.isLocalStorageAvailable()) {
                return 0;
            }

            let size = 0;
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    const value = localStorage.getItem(key);
                    if (value) {
                        // Calcular tamaño en bytes (aproximado)
                        size += key.length + value.length;
                    }
                }
            }
            return size * 2; // Multiplicar por 2 porque JavaScript usa UTF-16
        } catch (error) {
            console.error('Error al calcular tamaño:', error);
            return 0;
        }
    }

    // ==================== Métodos Auxiliares ====================

    /**
     * Obtiene el tamaño del almacenamiento en formato legible
     */
    getSizeFormatted(): string {
        const size = this.getSize();
        if (size < 1024) {
            return `${size} bytes`;
        } else if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(2)} KB`;
        } else {
            return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        }
    }

    /**
     * Obtiene estadísticas del almacenamiento
     */
    getStats(): {
        totalKeys: number;
        size: string;
        available: boolean;
    } {
        return {
            totalKeys: this.getAllKeys().length,
            size: this.getSizeFormatted(),
            available: this.isLocalStorageAvailable()
        };
    }

    /**
     * Exporta todo el contenido del localStorage
     * Útil para debugging o respaldos
     */
    export(): Record<string, unknown> {
        try {
            if (!this.isLocalStorageAvailable()) {
                return {};
            }

            const data: Record<string, unknown> = {};
            const keys = this.getAllKeys();

            for (const key of keys) {
                const value = localStorage.getItem(key);
                if (value) {
                    try {
                        data[key] = JSON.parse(value);
                    } catch {
                        data[key] = value; // Si no es JSON, guardar como string
                    }
                }
            }

            return data;
        } catch (error) {
            console.error('Error al exportar localStorage:', error);
            return {};
        }
    }

    /**
     * Importa datos al localStorage
     * Útil para restaurar respaldos
     */
    import(data: Record<string, unknown>): void {
        try {
            if (!this.isLocalStorageAvailable()) {
                console.warn('localStorage no está disponible');
                return;
            }

            for (const [key, value] of Object.entries(data)) {
                this.setItem(key, value);
            }
        } catch (error) {
            console.error('Error al importar datos:', error);
            throw error;
        }
    }
}
